import { Box, Grid, Popover } from "@mui/material";
import classNames from "classnames";
import { isEqual, maxBy } from "lodash";
import { State } from "modules/shop_grid/types";
import React, { useState } from "react";
import ArrowRightIcon from "@mui/icons-material/ArrowRightAlt";
import BaseCell from "./BaseCell";
import { useTranslation } from "react-i18next";
import "./GridCell.css";

interface GridCellProps {
  mode: "inventory" | "furniture";
  cell: State.Cell;
  dimmed?: boolean;
  onClick?: () => void;
  selected?: boolean;
  interactive?: boolean;
  showDiff?: boolean;
  size?: "small" | "medium";
  viewMode?: boolean;
  modeCell?: "comparator";
}

const TEXT_SIZES = ["0px", "14px", "10px", "12px"];

const computeFontSize = (cell: State.Cell) => {
  const largestSection =
    maxBy(
      cell.sections.map((section) => section.inventories),
      (inventories) => inventories.length
    )?.length || 0;

  return TEXT_SIZES[largestSection];
};

const findCompleteInventory = (cell: State.Cell) =>
  cell.sections
    .map((section) => section.inventories)
    .flat()
    .find((inventory) => inventory.family && inventory.subFamily);

const getSectionsInventory = (sections: State.Section[]) =>
  sections
    .flatMap((section) => section.inventories)
    .map((inventory) => [inventory.family?.id, inventory.subFamily?.id, inventory.quantity]);

const sectionsChanged = (sections1: State.Section[], sections2: State.Section[]) =>
  !isEqual(getSectionsInventory(sections1), getSectionsInventory(sections2));

interface DiffPopoverCellProps {
  sections: State.Section[];
  defaultColor: string;
}
interface DiffPopoverProps {
  open: boolean;
  anchor: HTMLElement | null | undefined;
  onClose: () => void;
  cell: State.Cell;
}

const DiffPopoverCell = ({ sections, defaultColor }: DiffPopoverCellProps) => {
  return ( <Grid container sx={{ border: '1px solid black', flexDirection: 'column', width: 'auto'}}>
    {sections.map((section, indexSection) => (
      <Grid item container key={indexSection}>
        {section.inventories.map((inventory, index) => (
          <Grid item key={index}>
            <BaseCell style={{ margin: 0.5 }} color={inventory.family?.color || defaultColor}>{inventory.subFamily?.shortName || ''}</BaseCell>
          </Grid>
        ))}
      </Grid>
    ))}
  </Grid>);
};

const DiffPopover = ({ open, anchor, onClose, cell }: DiffPopoverProps) => {
  return ( <Popover
    sx={{ pointerEvents: "auto" }}
    open={open}
    anchorEl={anchor}
    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    transformOrigin={{ vertical: "top", horizontal: "left" }}
    onClose={onClose}
    disableRestoreFocus
  >
    <Box sx={{ p: 1, display: "flex", alignItems: "center", zoom: 2 }}>
      <DiffPopoverCell sections={cell.originalSections!} defaultColor={cell.furniture?.color || "#767676"} />
      <ArrowRightIcon />
      <DiffPopoverCell sections={cell.sections} defaultColor={cell.furniture?.color || "#767676"} />
    </Box>
  </Popover>);
};

const GridCell = ({
  mode,
  cell,
  dimmed,
  onClick,
  selected,
  size = "medium",
  interactive = false,
  showDiff = false,
  viewMode = false,
  modeCell,
}: GridCellProps) => {
  const { t } = useTranslation();
  const [popoverEl, setPopoverEl] = useState<HTMLElement | null>(null);

  let blocked = Boolean(mode === "inventory" && cell?.furniture && !cell.furniture.isRecordable);
  blocked = viewMode && !blocked ? !Boolean(findCompleteInventory(cell)) : blocked;
  const color =
    (mode === "inventory" && findCompleteInventory(cell)?.family?.color) || cell.furniture?.color || "#767676";
  const fontSize = mode === "inventory" ? computeFontSize(cell) : "14px";

  const changed = cell.originalSections ? sectionsChanged(cell.originalSections, cell.sections) : false;
  const readableInventory = cell.sections
    .flatMap((section) => section.inventories)
    .map((inventory) => inventory.subFamily?.shortName)
    .filter(Boolean)
    .join(", ");
  const cellAriaLabel = readableInventory
    ? t("a11y.gridCellWithContent", { row: cell.yAxis + 1, column: cell.xAxis + 1, content: readableInventory })
    : t("a11y.gridCell", { row: cell.yAxis + 1, column: cell.xAxis + 1 });

  const inventoryLabel = (sections: State.Section[]) => {
    const maxInventory = sections.reduce((prevValue, currentObject) => Math.max(prevValue, currentObject.inventories.length), 0)

    if (sections.length < 3 && maxInventory < 3) {
      return sections.map((section, i) => (
        <div key={i}>
          {section.inventories.map((inventory, i) => (
            <span key={i}>
              {inventory.subFamily?.shortName}
              {i !== section.inventories.length - 1 ? ";" : ""}
            </span>
          ))}
        </div>
      ))
    }

    return (<div><span>
      [{sections.map((section) => section.inventories.length).join(', ')}]
    </span></div>)
  };

  return (
    <BaseCell
      className={classNames("gridCell", { blocked, interactive })}
      color={color}
      onClick={() => !blocked && onClick?.()}
      blocked={blocked}
      changed={showDiff && changed}
      dimmed={dimmed}
      isHighlight={cell.isHighlight}
      selected={selected}
      interactive={interactive}
      size={size}
      style={{ gridRow: cell.yAxis + 2, gridColumn: cell.xAxis + 2, fontSize }}
      onMouseEnter={(e: React.MouseEvent<HTMLElement>) => setPopoverEl(e.currentTarget)}
      onMouseLeave={() => setPopoverEl(null)}
      onFocus={(e: React.FocusEvent<HTMLElement>) => setPopoverEl(e.currentTarget)}
      onBlur={() => setPopoverEl(null)}
      onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === "Escape") setPopoverEl(null);
      }}
      ariaLabel={cellAriaLabel}
      modeCell={modeCell}
    >
      {mode === "inventory" && (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {inventoryLabel(cell.sections)}
        </div>
      )}
      {mode === "furniture" && cell.shelvesCount > 1 && <div>{`(${cell.shelvesCount})`}</div>}
      {showDiff && changed && (
        <DiffPopover open={Boolean(popoverEl)} anchor={popoverEl} onClose={() => setPopoverEl(null)} cell={cell} />
      )}
    </BaseCell>
  );
};

export default GridCell;
